import { Component, useEffect, useState, type ComponentType, type ReactNode } from 'react';

/**
 * Remote card loader — the no-duplication seam.
 *
 * ESG's card components and registry live ONLY in brew-mfe, which exposes them
 * as a Module Federation remote (`brew_mfe/cards`). Brew loads that registry at
 * runtime and renders whatever component `resolveCard(action)` returns. Brew
 * holds zero card source, so ESG can add/change cards by rebuilding its remote
 * with NO Brew change or redeploy.
 *
 * The `action + body` shape is the stable contract; everything else is owned by
 * the product. If the remote is unreachable or a card throws, we fall back to
 * the markdown reply — the chat never breaks.
 */

/** Structured tool-result sidecar (delta.tool_results). The stable seam between
 *  Brew (transport/persistence) and the product (card rendering). */
export interface ToolResultSidecar {
  action: string;
  args: Record<string, unknown>;
  body: unknown;
  status: number;
  /** Card-owner binding (Part E). Stamped by the server on interactive cards: a
   *  click on one of this card's options echoes these back as a CardReply, which
   *  the server validates and pins to `ownerConnectorId` (precedence rule #1). */
  cardId?: string;
  ownerConnectorId?: string;
  flowId?: string;
}

/** Props every remote card receives. Mirrors brew-mfe's CardProps so the remote
 *  components type-check against the same shape. */
export interface CardProps {
  body: unknown;
  args?: Record<string, unknown>;
  status?: number;
  onSelect?: (text: string) => void;
}

type ResolveCard = (action: string) => ComponentType<CardProps> | null;
const NULL_RESOLVE: ResolveCard = () => null;

// Pull `resolveCard` out of the remote module regardless of how the federation
// runtime hands it back. ESG's cards index exports `resolveCard` as a NAMED
// export with NO default, so the federation runtime's wrapDefault() buries the
// real namespace under `.default` (an obj whose own `.resolveCard` is undefined).
// Check the namespace AND `.default` so both wrapped and unwrapped shapes work.
function pickResolveCard(mod: unknown): ResolveCard {
  for (const ns of [mod, (mod as { default?: unknown } | null)?.default]) {
    const fn = (ns as { resolveCard?: unknown } | null | undefined)?.resolveCard;
    if (typeof fn === 'function') return fn as ResolveCard;
  }
  return NULL_RESOLVE;
}

// Load ESG's card registry exactly once. A failed remote load degrades to a
// resolver that yields no cards (→ markdown fallback), so the chat still works
// when brew-mfe is down or unreachable.
let registryPromise: Promise<{ resolveCard: ResolveCard }> | null = null;
function loadRegistry(): Promise<{ resolveCard: ResolveCard }> {
  if (!registryPromise) {
    registryPromise = (import('brew_mfe/cards') as Promise<unknown>)
      .then((mod) => {
        const resolveCard = pickResolveCard(mod);
        if (resolveCard === NULL_RESOLVE) {
          console.warn('[remote-cards] brew_mfe/cards loaded but resolveCard not found — keys:', mod && Object.keys(mod as object));
        } else {
          console.info('[remote-cards] ESG card registry loaded from brew_mfe/cards');
        }
        return { resolveCard };
      })
      .catch((err: unknown) => {
        console.warn('[remote-cards] failed to load brew_mfe/cards remote — falling back to markdown', err);
        return { resolveCard: NULL_RESOLVE };
      });
  }
  return registryPromise;
}

/**
 * Returns the remote `resolveCard` once ESG's registry has loaded. Until then it
 * returns a resolver that yields `null`, so the markdown reply shows first and
 * the cards swap in on the next render (the registry is cached after the first
 * load, so subsequent turns render cards immediately).
 */
export function useRemoteResolveCard(): ResolveCard {
  const [resolve, setResolve] = useState<ResolveCard>(() => NULL_RESOLVE);
  useEffect(() => {
    let alive = true;
    void loadRegistry().then((reg) => {
      if (alive) setResolve(() => reg.resolveCard);
    });
    return () => { alive = false; };
  }, []);
  return resolve;
}

/**
 * Isolates a single remote card render. If a card throws (malformed payload, a
 * bug in the remote), we render nothing instead of crashing the whole chat —
 * the markdown reply beneath still shows.
 */
export class CardBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override componentDidCatch(err: unknown): void {
    console.warn('[remote-cards] card render error — falling back to markdown', err);
  }
  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}
